import { Field, useFormikContext } from "formik";
import React, { FC, useEffect, useState } from 'react';
import { toast } from "react-toastify";
import Togglebox from "../../components/togglebox/togglebox";
import { CERTIFICATE_IS_CA, CERTIFICATE_STATUS, CERTIFICATE_TYPES } from "../../constants/certificate-constants";
import { COMMAND_STATE } from '../../constants/command-status';
import Commands from '../../constants/commands';
import { AccountSubCmds } from '../../constants/sub-commands';
import { TranslationKeys } from "../../contexts/TranslationKeys";
import { Button } from '../../components/button/Button';
import { useLocale } from "../../contexts/localeContext";
import { ExecuteCommand } from '../../models/ExecuteCommand';
import { ListQuery } from "../../models/ListQuery";
import { executeCommand } from '../../services/executeCommandService';
import { getCertificateDetailsById, getCertificateNamesWithPagination } from '../../services/securityPolicyService';
import { getErrorMessage } from '../../services/utilities/ServiceError';
import { restrictToNumbersOnly, validateItemName } from "../../utilities/Validation";
import { appendFilter } from "../../utilities/filters";
import { useDialog } from "../dialog/DialogContext";
import Label from "../label/label";
import PaginatedSearchableSelectField from '../selection-field/paginated-searchable-select-field';

interface MqttServiceAccountCardProps {
    isLicensed: boolean,
    currentItem: any,
    isAccountPopulated: boolean, // Whether all the required details are populated to allow querying
    setBusy(busy: boolean): void
}

const MqttServiceAccountCard: FC<MqttServiceAccountCardProps> = ({ isLicensed, currentItem, isAccountPopulated, setBusy }) => {
    const { errors, values, setFieldValue, dirty } = useFormikContext<any>();
    const { translate } = useLocale();
    const [primoCert, setPrimoCert] = useState<string>('');
    const dialog = useDialog();
    let shouldRefreshLabel = true;

    useEffect(() => {
        if (values.details.tlsPrimoCert.value && shouldRefreshLabel) {
            setSelectedItemLabel();
        }
    }, [values.details.tlsPrimoCert.value]);

    /**
    * The account is queried using saved account details only. Therefore, to query the account:
    * 1. Must be licensed.
    * 2. The account details must be populated.
    * 3. There must be no unsaved changes.
    * @returns 
    */
    const canTestAccount = () => {
        return isLicensed && isAccountPopulated && !dirty;
    }

    const setSelectedItemLabel = async () => {
        const primoCertName = (await getCertificateDetailsById(parseInt(values.details.tlsPrimoCert.value), translate)).data.name;
        setPrimoCert(primoCertName);
    }

    const errorHandler = (accessor) => {
        const error = accessor.split('.').reduce((previous, current) => { return previous?.[current] }, errors as any);
        if (error) {
            return <span className='text-red-600 text-xs -ml-3 pr-8'>{error}</span>
        } else {
            return null;
        }
    };

    const onEnabled = () => {
        const newEnabledState = !values.enabled;

        if (newEnabledState && !isLicensed) {
            dialog.pushError({ title: translate.byKey(TranslationKeys.unlicensed), message: translate.byKey(TranslationKeys.unlicensedFeature) });
            setFieldValue("enabled", false);
            return;
        }

        setFieldValue("enabled", newEnabledState);
    }

    const onTestAccount = () => {
        setBusy(true);
        const command: ExecuteCommand = {
            cmd: Commands.Account.label,
            cmdSubType: AccountSubCmds.Test_Account,
            entityId: currentItem.id,
            jsonResponse: ""
        }

        executeCommand(command).then((response: any) => {
            if (response.status === COMMAND_STATE.COMMAND_COMPLETE) {
                if (values.enabled === false) {
                    toast.success(translate.byKey(TranslationKeys.successConnectedToDisabledAccount));
                }
                else {
                    toast.success(translate.byKey(TranslationKeys.successConnectedToAccount));
                }
            }
            else {
                dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: translate.byKey(TranslationKeys.testAccountFailed) });
            }
        }).catch((error) => {
            dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: getErrorMessage(error) });
        }).finally(() => {
            setBusy(false);
        });
    }

    return <div className="w-full p-2 inline-flex justify-start items-top bg-white">

        {/* Left-hand column */}
        <div className="flex flex-col py-4 px-8">

            {/* Client Id - a unique identifier for the MQTT service to identify Primo connection */}
            <div className="mb-2">
                <div className="flex flex-row justify-between" id="tlsClientIdLabel">
                    <Label htmlFor="details.tlsClientId.value" className="text-xs text-gray-600 mt-1" required>{translate.byKey(TranslationKeys.clientId)}:</Label>
                    {errorHandler("details.tlsClientId.value")}
                </div>
                <div className="flex flex-row justify-between">
                    <Field id='tlsClientIdInputFieldId'
                        name="details.tlsClientId.value"
                        type={'text'}
                        className="flex flex-row items-center p-1 text-gray-800 text-sm bg-gray-input"
                        style={{ width: '17rem' }}
                        validate={(value) => validateItemName(value, translate)} />
                </div>
            </div>

            {/* TLS URL - the URL of the MQTT broker */}
            <div className="mb-2">
                <div className="flex flex-row justify-between" id="tlsUrlLabel">
                    <Label htmlFor="details.tlsUrl.value" className="text-xs text-gray-600 mt-1" required>{translate.byKey(TranslationKeys.tlsUrl)}:</Label>
                    {errorHandler("details.tlsUrl.value")}
                </div>
                <div className="flex flex-row justify-between">
                    <Field id='tlsUrlInputFieldId'
                        name="details.tlsUrl.value"
                        type={'text'}
                        className="flex flex-row items-center p-1 text-gray-800 text-sm bg-gray-input"
                        style={{ width: '17rem' }}
                        validate={(value) => validateItemName(value, translate)} />
                </div>
            </div>

            {/* TLS Port - the port of the MQTT broker */}
            <div>
                <div className="flex flex-row justify-between mb-1" id="tlsPortLabel">
                    <Label
                        htmlFor="details.tlsPort.value"
                        className="text-xs text-gray-600 mt-1" required>{translate.byKey(TranslationKeys.tlsPort)}:</Label>
                    {errorHandler("details.tlsPort.value")}
                </div>
                <div className="flex flex-row justify-between">
                    <Field id='tlsPortInputFieldId'
                        type={'text'}
                        name="details.tlsPort.value"
                        className="flex flex-row items-center p-1 text-gray-800 text-sm bg-gray-input"
                        style={{ width: '17rem' }}
                        onKeyDown={restrictToNumbersOnly}
                        validate={(value) => validateItemName(value, translate)} />
                </div>
            </div>
        </div>

        {/* Right-hand column */}
        <div className="flex flex-col py-4 px-8">

            {/* Certificate to connect to MQTT broker */}
            <div className="mb-2">
                <Label htmlFor='certificateType'
                    className="text-xs text-gray-600 mt-1" required>{translate.byKey(TranslationKeys.primoCertificate)}:</Label>

                <PaginatedSearchableSelectField
                    name={translate.byKey(TranslationKeys.primoCertificate)}
                    key='certificateType'
                    accessor='details.tlsPrimoCert.value'
                    dataMethod={(listParameters?: ListQuery) => getCertificateNamesWithPagination({ ...listParameters,
                        filters: appendFilter(CERTIFICATE_IS_CA.NO, 'Certificate CA', 'certificateAuthority',
                            appendFilter(CERTIFICATE_TYPES.SOFTWARE_MQTT,'Certificate Type', 'ksCertificateType.id',
                            appendFilter(CERTIFICATE_STATUS.REVOKED,'Certificate Status', 'ksCertificateStatus.id',[], "=out=")))
                    })}
                    searchAccessor={'trnName'}
                    required={true}
                    selectedItemLabel={primoCert}
                    onSelect={(_, value) => {
                        shouldRefreshLabel = false;
                        setFieldValue('details.tlsPrimoCert.certName', value?.label);
                        setFieldValue('details.tlsPrimoCert.value', value?.value);
                    }} />
            </div>

            {/* Enable the TLS connection - just allows us to use it. In future we'd like to prevent enabling if we can't connect */}
            <div className="mb-2">
                <div className="flex flex-row justify-between" id="enabled">
                    <Label htmlFor="enabled" className="text-xs text-gray-600 mt-1" required>
                        {translate.byKey(TranslationKeys.enabled)}:
                    </Label>
                </div>
                <Togglebox name="enabled" onChanged={onEnabled} label={""} />
            </div>

            <div className="mb-2 mt-1">
                <Button
                    variant="tertiary"
                    shape="rounded"
                    className="h-8 px-2 mt-1"
                    disabled={!canTestAccount()}
                    onClick={onTestAccount}
                    id="testQueryButton">
                    <span className="mr-2">{translate.byKey(TranslationKeys.testAccount)}</span>
                </Button>
            </div>

            
        </div>
    </div>
}

export default MqttServiceAccountCard;
